// @djsem/ui — shared design tokens (single source for cross-app theming).
//
// The palette matches the djsem.ai landing: deep near-black, refined champagne gold accent
// (never neon), muted slate text. Web app, desktop app, and landing stay visually coherent.
// Every pre-existing key is preserved (bg/surface/border/text/muted/accent/accentCyan,
// radius.sm/md, font.sans, space) so this is a drop-in refinement, not a breaking change.
//
// Task #22 (Djoid-inspired theme pass): `radius.full` (pill buttons, matching Djoid's
// rounded-full CTA convention — see shellStyles.ts's .djsem-btn) and `camelotWheel` (the
// 12-hue rainbow Djoid uses for track-key/energy/danceability chips — see
// components.tsx's KeyChip) are additive. DJSEM keeps its own champagne-gold identity
// rather than adopting Djoid's neutral white-pill palette wholesale — "inspired by", not a
// literal reskin, since the gold accent is already the established brand across the landing
// page, admin console, and every other surface built this project.

/** Shared design tokens (colors, radii, fonts, type scale, spacing, shadows) used across web/desktop/landing. */
export const tokens = {
  color: {
    // core surfaces
    bg: "#08090c",           // page background (deep near-black)
    surface: "#0e1119",      // panels / cards
    surface2: "#12161f",     // raised panels, hover fills
    surfaceHi: "#171c26",    // highest elevation (palette, menus)
    border: "#1a1f2b",       // hairline borders
    borderHi: "#232937",     // hover/active borders
    // text
    text: "#e6e6e2",         // primary text (warm off-white)
    textDim: "#a9adb7",      // secondary text
    muted: "#6c7280",        // tertiary / labels
    // accent
    accent: "#c9a35a",       // champagne gold — signature
    accentHi: "#e6c07a",     // hover gold
    accentDim: "#5a4a2b",    // gold borders / subtle fills
    accentCyan: "#34e0e0",   // legacy cyan (kept for compatibility; use sparingly)
    // semantic
    ok: "#7fce9e",
    okDim: "#1f5236",
    warn: "#d9a253",
    warnDim: "#52402a",
    danger: "#e08a8a",
    dangerDim: "#52262a",
  },
  radius: { sm: "3px", md: "6px", lg: "10px", full: "999px" },
  font: {
    sans: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    serif: '"Cormorant Garamond", "Playfair Display", ui-serif, Georgia, serif',
    mono: 'ui-monospace, "JetBrains Mono", "SF Mono", Menlo, monospace',
  },
  text: {
    // type scale (px) — one place to keep hierarchy consistent
    xs: 11, sm: 12, base: 13, md: 14, lg: 16, xl: 20, xxl: 26,
  },
  space: (n: number) => `${n * 4}px`,
  shadow: {
    panel: "0 1px 2px rgba(0,0,0,0.3)",
    overlay: "0 12px 48px rgba(0,0,0,0.55)",
  },
  ease: "cubic-bezier(0.22, 0.61, 0.36, 1)",
} as const;

// Camelot wheel colors (1-12), the same rainbow convention Djoid uses for its track-metadata
// chips (key/energy/danceability). Index 0 = position 1, ... index 11 = position 12; A/B
// (minor/major) share a hue since they're adjacent on the wheel. Muted/desaturated to sit
// comfortably against the near-black background rather than Djoid's brighter saturation.
export const CAMELOT_WHEEL_COLORS: readonly string[] = [
  "#e85d75", "#e8735d", "#d98a4a", "#c9a35a", "#a3b356", "#7fbf6b",
  "#5cc2a0", "#4fb8c4", "#5a9bd9", "#7d84e0", "#a374d9", "#c95dc0",
];

/**
 * Maps a Camelot key string ("8A", "3B", ...) to its wheel color. Unknown/malformed input falls back to `tokens.color.muted`.
 * @param camelotKey - The Camelot code (only the leading hour number is read).
 * @returns The wheel color hex string, or `tokens.color.muted` if malformed/out of range.
 */
export function camelotColor(camelotKey: string): string {
  const n = parseInt(camelotKey, 10);
  if (!Number.isFinite(n) || n < 1 || n > 12) return tokens.color.muted;
  return CAMELOT_WHEEL_COLORS[n - 1];
}
